import exportFromJSON from "export-from-json";
import { useTranslation } from "react-i18next";
import userSettingsService from "src/services/userSettingsService";
import store from "src/store";
import { useSelector } from "src/store";
import { useLicenses } from "src/ui/containers/dashboard/licenses/api/helpers";
import { useAnalyticsClick } from "src/ui/hooks/useAnalyticsClick";

import { DropdownItem } from "@dashboard/devices/components/shared/Dropdown/DropdownItem";
import { filterLicensesByProperties } from "@dashboard/devices/store/helpers/index";
import { selectDevicesByLicenseIds } from "@dashboard/devices/store/selectors/devices";
import { selectGroups } from "@dashboard/groups/store/selectors";
import LicensesSelectors from "@dashboard/licenses/store/selectors";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Device, License } from "@devices/types";
import { showSuccessToast } from "@shared/toasts/Toasts";

type FilterStateProperties = {
    licenseStatus: string[];
    onlineStatus: string[];
    policyStatus: string[];
    systemVersion: string[];
    codename: string[];
    groupName: string[];
};

type CsvRow = Record<string, string | number>;

function getFilterProperties(): FilterStateProperties {
    return userSettingsService
        .getDevicePageColumnFilters()
        .reduce((acc: FilterStateProperties, filter) => ({ ...acc, [filter.id]: filter.value as string[] }), {
            licenseStatus: [],
            onlineStatus: [],
            policyStatus: [],
            systemVersion: [],
            codename: [],
            groupName: [],
        });
}

function buildCsvRows(licenses: License[], devices: Device[]): CsvRow[] {
    const devicesById = new Map(devices.map((device) => [device.id, device]));
    return licenses.map((license) => {
        const device = license.device ? devicesById.get(license.device.id) : undefined;
        return {
            "Comment": license.comment ?? "",
            "Charge ID": license.chargeId,
            "Code": license.activationCode,
            "License created": license.creationDate,
            "Device registered": license.associationDate ?? "",
            "Registration IP": license.associatorIp ?? "",
            "Group": license.groupName,
            "Universal group": license.universalGroupName ?? "",
            "Device ID": license.device?.id ?? "",
            "Device IP": device?.lastState?.ethernet?.ip ?? "",
            "Connected SSID": device?.lastState?.wifi?.connected ? (device.lastState.wifi.ssid ?? "") : "",
            "Name": device?.name ?? "",
            "Description": device?.description ?? "",
            "Codename": device?.product ?? "",
            "Serial": device?.serial ?? "",
        };
    });
}

// `escapeFormulae` is off by default. Exported cells include names, descriptions, Wi-Fi network names and IP
// addresses, which are reported by the devices themselves; a spreadsheet treats a cell starting with = + - or @
// as a formula to run when the file is opened.
function exportCsv(rows: CsvRow[]): void {
    exportFromJSON({
        data: rows,
        fields: Object.keys(rows[0]),
        fileName: "devices",
        exportType: exportFromJSON.types.csv,
        escapeFormulae: true,
    });
}

const ExportCSV = () => {
    const trackClick = useAnalyticsClick();
    const { t } = useTranslation(["pages"]);
    const allLicenses = useLicenses();
    const groups = useSelector(selectGroups);
    const isExpired = useCurrentSubscriptionExpired();
    const licenseStatus = useSelector(LicensesSelectors.selectLicensesStatus);
    const isLoading = licenseStatus === "pending" || licenseStatus === "updating";

    const handleClick = () => {
        const filters = getFilterProperties();
        const filteredLicenses = filterLicensesByProperties({ licenses: allLicenses, filters, groups });
        if (!filteredLicenses.length) {
            return;
        }

        const licenseIds = filteredLicenses.map((l) => l.id);
        const devices = selectDevicesByLicenseIds(store.getState(), licenseIds);

        exportCsv(buildCsvRows(filteredLicenses, devices));
        showSuccessToast(t("pages:devicePage.toast.exported", { count: filteredLicenses.length }));
    };

    return (
        <DropdownItem
            label={t("pages:devicePage.button.exportLicenses")}
            iconName="fa-solid fa-download"
            onClick={trackClick(handleClick, "export-licenses", "dropdown")}
            // All CSV data is computed lazily on click, so we only check allLicenses here rather than
            // filtered licenses. If the active filters happen to produce zero results, clicking Export
            // silently does nothing — acceptable given the significant render-time savings.
            disabled={!allLicenses.length || isExpired || isLoading}
            tooltipProps={{
                title: isExpired
                    ? t("pages:devicePage.tooltip.subscriptionExpired")
                    : t("pages:devicePage.tooltip.devicesStillLoading"),
                hide: !isExpired && !isLoading,
            }}
        />
    );
};

export default ExportCSV;
